import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { Trash2, CheckCircle } from "lucide-react";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useToast } from "@/hooks/use-toast";
import { EarnedBonusDTO, BonusType, BonusTypeLabels } from "@/types/bonus";
import { BonusTypePill } from "@/components/admin/bonuses/BonusTypePill";
import { PageResponse } from "@/types/page";

const PAGE_SIZE = 20;

export default function EarnedBonusReport() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [userId, setUserId] = useState(searchParams.get("userId") ?? "");
  const [bonusId, setBonusId] = useState(searchParams.get("bonusId") ?? "");
  const [bonusType, setBonusType] = useState<BonusType | "__all__">(
    (searchParams.get("bonusType") as BonusType) || "__all__",
  );
  const [paid, setPaid] = useState<"__all__" | "true" | "false">(
    (searchParams.get("paid") as "true" | "false") || "__all__",
  );
  const [earnedFrom, setEarnedFrom] = useState(
    searchParams.get("earnedFrom") ?? "",
  );
  const [earnedTo, setEarnedTo] = useState(searchParams.get("earnedTo") ?? "");
  const [page, setPage] = useState(0);

  const queryKey = [
    "earned-bonuses",
    { userId, bonusId, bonusType, paid, earnedFrom, earnedTo, page },
  ];

  const { data, isLoading, error } = useQuery<PageResponse<EarnedBonusDTO>>({
    queryKey,
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(page),
        size: String(PAGE_SIZE),
      };
      if (userId) params.userId = userId;
      if (bonusId) params.bonusId = bonusId;
      if (bonusType !== "__all__") params.bonusType = bonusType;
      if (paid !== "__all__") params.paid = paid;
      if (earnedFrom) params.earnedFrom = earnedFrom;
      if (earnedTo) params.earnedTo = earnedTo;
      const res = await axiosInstance.get("/earned-bonus", { params });
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => axiosInstance.delete(`/earned-bonus/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["earned-bonuses"] });
      toast({
        title: "Deleted",
        description: "Record deleted.",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete.",
        variant: "destructive",
      });
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: (id: string) =>
      axiosInstance.put(`/earned-bonus/${id}/mark-paid`),
    onSuccess: (_response, id) => {
      // Updated in place, not reloaded. A reload can reorder the rows or, under
      // the Unpaid filter, drop this one and slide the next row up, so the
      // button under the admin's cursor would belong to someone else.
      // Everything else is marked stale for next time without refetching now.
      queryClient.invalidateQueries({
        queryKey: ["earned-bonuses"],
        refetchType: "none",
      });
      queryClient.setQueryData<PageResponse<EarnedBonusDTO>>(
        queryKey,
        (current) =>
          current && {
            ...current,
            content: current.content.map((rec) =>
              rec.id === id
                ? { ...rec, paid: true, paidDate: new Date().toISOString() }
                : rec,
            ),
          },
      );
      toast({
        title: "Marked Paid",
        description: "Record marked as paid.",
        variant: "success",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to mark as paid.",
        variant: "destructive",
      });
    },
  });

  const handleSearch = () => {
    setPage(0);
    const params: Record<string, string> = {};
    if (userId) params.userId = userId;
    if (bonusId) params.bonusId = bonusId;
    if (bonusType !== "__all__") params.bonusType = bonusType;
    if (paid !== "__all__") params.paid = paid;
    if (earnedFrom) params.earnedFrom = earnedFrom;
    if (earnedTo) params.earnedTo = earnedTo;
    setSearchParams(params);
  };

  const records = data?.content ?? [];

  return (
    <div className="container mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold">Earned Bonus Records</h1>

      {/* Filters */}
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
        <div className="space-y-1">
          <label className="text-sm font-medium">User ID</label>
          <Input
            placeholder="User ID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Bonus ID</label>
          <Input
            placeholder="Bonus ID"
            value={bonusId}
            onChange={(e) => setBonusId(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Type</label>
          <Select
            value={bonusType}
            onValueChange={(v) => setBonusType(v as BonusType | "__all__")}
          >
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All types</SelectItem>
              {Object.values(BonusType).map((t) => (
                <SelectItem key={t} value={t}>
                  {BonusTypeLabels[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Paid</label>
          <Select
            value={paid}
            onValueChange={(v) => setPaid(v as "__all__" | "true" | "false")}
          >
            <SelectTrigger>
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="false">Unpaid</SelectItem>
              <SelectItem value="true">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Earned From</label>
          <Input
            type="date"
            value={earnedFrom}
            onChange={(e) => setEarnedFrom(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Earned To</label>
          <Input
            type="date"
            value={earnedTo}
            onChange={(e) => setEarnedTo(e.target.value)}
          />
        </div>
      </div>
      <Button onClick={handleSearch}>Search</Button>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Bonus</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Referred</TableHead>
              <TableHead>Earned Date</TableHead>
              <TableHead>Paid</TableHead>
              <TableHead>Paid Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-destructive">
                  Failed to load records.
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground"
                >
                  No records found.
                </TableCell>
              </TableRow>
            ) : (
              records.map((rec) => (
                <TableRow key={rec.id}>
                  <TableCell className="font-medium">{rec.userName}</TableCell>
                  <TableCell>{rec.bonusTitle}</TableCell>
                  <TableCell>
                    <BonusTypePill type={rec.bonusType} />
                  </TableCell>
                  <TableCell>
                    {rec.dollarAmount != null ? `$${rec.dollarAmount}` : "—"}
                  </TableCell>
                  <TableCell>{rec.referredUserName ?? "—"}</TableCell>
                  <TableCell>
                    {format(new Date(rec.earnedDate), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    {rec.paid ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-success/10 text-success">
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-warning/10 text-warning">
                        No
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {rec.paidDate
                      ? format(new Date(rec.paidDate), "MMM d, yyyy")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {/* A paid row keeps a button in this slot, so Delete does
                          not slide under a cursor that just clicked Mark Paid. */}
                      {rec.paid ? (
                        <Button size="sm" variant="outline" disabled>
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          Paid
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markPaidMutation.mutate(rec.id)}
                          disabled={markPaidMutation.isPending}
                        >
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          Mark Paid
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          if (
                            window.confirm("Delete this earned bonus record?")
                          ) {
                            deleteMutation.mutate(rec.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {data && data.page.totalPages > 1 && (
        <div className="flex items-center gap-2 justify-end">
          <Button
            variant="outline"
            size="sm"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page + 1} of {data.page.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= data.page.totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
